"use client";

import React, { useState } from "react";
import { 
  Building, 
  Gavel, 
  Calendar, 
  FileText, 
  GripVertical, 
  MessageSquare, 
  ShieldCheck, 
  ChevronRight, 
  ChevronLeft,
  DollarSign,
  AlertTriangle,
  FolderOpen
} from "lucide-react";
import { LegalCase } from "@/lib/demos/legalData";

interface LegalKanbanProps {
  cases: LegalCase[];
  userRole: "partner" | "associate" | "clerk";
  onSelectCase: (c: LegalCase) => void;
  onAdvanceStage: (c: LegalCase, targetStage: LegalCase["stage"]) => void;
  onOpenNotification: (c: LegalCase) => void;
}

interface StageConfig {
  id: LegalCase["stage"];
  title: string;
  color: string;
  bg: string;
  border: string;
  hint: string;
}

const STAGES: StageConfig[] = [
  {
    id: "New Case Intake",
    title: "1. New Intake",
    color: "text-blue-400",
    bg: "bg-blue-500/10",
    border: "border-blue-500/20",
    hint: "Conflict check & preliminary intake screening",
  },
  {
    id: "Consultation & Retainer",
    title: "2. Consultation & Retainer",
    color: "text-violet-400",
    bg: "bg-violet-500/10",
    border: "border-violet-500/20",
    hint: "Engagement letter signed & advance retainer collected",
  },
  {
    id: "Drafting & Evidence",
    title: "3. Drafting & Evidence",
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/20",
    hint: "Plaint, Vakalatnama & witness affidavits preparation",
  },
  {
    id: "Filed in Court / Listed",
    title: "4. Filed & Listed",
    color: "text-cyan-400",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500/20",
    hint: "Registry scrutiny cleared & Diary / CNR issued",
  },
  {
    id: "Hearing & Arguments",
    title: "5. Hearing & Arguments",
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/20",
    hint: "Active appearances, interim stays & cross-examination",
  },
  {
    id: "Decree & Closed (Won)",
    title: "6. Decree & Closed (Won)",
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/20",
    hint: "Final judgment decreed & execution warrant executed",
  },
];

export default function LegalKanban({
  cases,
  userRole,
  onSelectCase,
  onAdvanceStage,
  onOpenNotification,
}: LegalKanbanProps) {
  const [draggingCaseId, setDraggingCaseId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [columnLimits, setColumnLimits] = useState<{ [key: string]: number }>({});

  const handleDragStart = (e: React.DragEvent, caseId: number) => {
    setDraggingCaseId(caseId);
    e.dataTransfer.setData("text/plain", String(caseId));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggingCaseId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStage !== stageId) {
      setDragOverStage(stageId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverStage(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetStage: LegalCase["stage"]) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverStage(null);
    const rawId = e.dataTransfer.getData("text/plain");
    const caseId = Number(rawId) || draggingCaseId;
    setDraggingCaseId(null);

    if (caseId) {
      const targetCase = cases.find((c) => c.id === caseId);
      if (targetCase && targetCase.stage !== targetStage) {
        onAdvanceStage(targetCase, targetStage);
      }
    }
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-6">
      {STAGES.map((stage, stageIdx) => {
        const stageCases = cases.filter((c) => c.stage === stage.id);
        const isTargeted = dragOverStage === stage.id;

        // Virtualized / Paged Slice for instant rendering
        const limitForStage = columnLimits[stage.id] || 25;
        const visibleCases = stageCases.slice(0, limitForStage);
        const hiddenCount = stageCases.length - visibleCases.length;

        // Calculate stage total value
        const inrTotal = stageCases.filter((c) => c.currency === "INR").reduce((sum, c) => sum + (c.retainer_amount || 0), 0);
        const usdTotal = stageCases.filter((c) => c.currency === "USD").reduce((sum, c) => sum + (c.retainer_amount || 0), 0);

        let valueDisplay = "$0";
        if (inrTotal > 0 && usdTotal > 0) {
          valueDisplay = `₹${inrTotal.toLocaleString("en-IN")} • $${usdTotal.toLocaleString()}`;
        } else if (inrTotal > 0) {
          valueDisplay = `₹${inrTotal.toLocaleString("en-IN")}`;
        } else {
          valueDisplay = `$${usdTotal.toLocaleString()}`;
        }

        return (
          <div
            key={stage.id}
            onDragOver={(e) => handleDragOver(e, stage.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage.id)}
            className={`flex flex-col flex-1 min-w-[300px] rounded-2xl transition-all duration-200 border p-3.5 h-[80vh] ${
              isTargeted
                ? "bg-amber-500/10 border-amber-500 ring-2 ring-amber-500/30 shadow-xl scale-[1.01]"
                : "bg-slate-900/60 border-slate-800"
            }`}
          >
            {/* Stage Column Header */}
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${stage.color.replace("text-", "bg-")}`} />
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-200">
                  {stage.title}
                </h3>
              </div>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${stage.bg} ${stage.color}`}>
                {stageCases.length}
              </span>
            </div>

            {/* Stage Value Metric (Partner only) */}
            {userRole === "partner" && (
              <div className="text-[11px] text-slate-400 mb-2 px-1 flex items-center justify-between">
                <span>Stage Retainers:</span>
                <span className="font-bold text-slate-200 font-mono text-[11px]">
                  {valueDisplay}
                </span>
              </div>
            )}

            {/* Stage Automation Hint */}
            <div className="text-[10px] text-slate-400 px-2 py-1 mb-2.5 rounded bg-slate-850/80 border border-slate-800 truncate" title={stage.hint}>
              ⚡ {stage.hint}
            </div>

            {/* Cards Scroll Container - Drop Target Anywhere */}
            <div
              onDragOver={(e) => handleDragOver(e, stage.id)}
              onDrop={(e) => handleDrop(e, stage.id)}
              className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[160px]"
            >
              {stageCases.length === 0 ? (
                <div
                  onDragOver={(e) => handleDragOver(e, stage.id)}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  className={`h-36 flex flex-col items-center justify-center border-2 border-dashed rounded-xl text-xs transition-all ${
                    isTargeted
                      ? "border-amber-500 text-amber-400 bg-amber-500/10 font-bold shadow-sm"
                      : "border-slate-800 text-slate-500 italic"
                  }`}
                >
                  {isTargeted ? (
                    <span>Drop anywhere here to move ⚡</span>
                  ) : (
                    <span>No active matters in stage</span>
                  )}
                </div>
              ) : (
                <>
                  {visibleCases.map((c) => {
                    const isDragging = draggingCaseId === c.id;

                    const progressSteps: Record<string, { step: number; pct: number }> = {
                      "New Case Intake": { step: 1, pct: 16 },
                      "Consultation & Retainer": { step: 2, pct: 33 },
                      "Drafting & Evidence": { step: 3, pct: 50 },
                      "Filed in Court / Listed": { step: 4, pct: 66 },
                      "Hearing & Arguments": { step: 5, pct: 83 },
                      "Decree & Closed (Won)": { step: 6, pct: 100 },
                    };
                    const progress = progressSteps[c.stage] || { step: 1, pct: 16 };

                    return (
                      <div
                        key={c.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, c.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, stage.id)}
                        onDrop={(e) => handleDrop(e, stage.id)}
                        onClick={() => onSelectCase(c)}
                        className={`group relative p-3.5 rounded-xl bg-slate-850 border border-slate-750 hover:border-amber-500/50 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
                          isDragging ? "opacity-30 scale-95 border-dashed border-amber-500" : ""
                        }`}
                      >
                        {/* Priority Badge, CNR & Drag Handle */}
                        <div className="flex items-center justify-between gap-1 mb-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                            c.urgency === "EMERGENCY"
                              ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                              : c.urgency === "HIGH"
                              ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                              : "bg-blue-500/10 text-blue-400 border-blue-500/20"
                          }`}>
                            {c.urgency === "EMERGENCY" ? "🚨 EMERGENCY" : c.urgency === "HIGH" ? "⚡ HIGH" : "NORMAL"}
                          </span>

                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[10px] text-slate-400">
                              #{c.case_number.split("/")[0]}
                            </span>
                            <GripVertical className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-colors" />
                          </div>
                        </div>

                        {/* Matter Title */}
                        <div className="flex items-start justify-between gap-1">
                          <h4 className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors line-clamp-1 flex-1">
                            {c.title}
                          </h4>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenNotification(c);
                            }}
                            title="Dispatch Automated Client Notice"
                            className="p-1 rounded-md text-emerald-400 hover:bg-emerald-500/10 transition-colors shrink-0 -mt-0.5"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Client vs Opposing Party */}
                        <p className="text-[11px] text-slate-400 mt-1 line-clamp-1">
                          <strong className="text-slate-300">{c.client_name}</strong> vs. {c.opposing_party}
                        </p>

                        {/* Court & Bench */}
                        <div className="mt-2 text-[10px] text-slate-400 space-y-0.5">
                          <div className="flex items-center gap-1 text-slate-300">
                            <Building className="w-3 h-3 text-amber-400/70 shrink-0" />
                            <span className="truncate">{c.court_name}</span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-400">
                            <Gavel className="w-3 h-3 text-slate-500 shrink-0" />
                            <span className="truncate">{c.court_room} • Item #{c.item_number}</span>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-2.5 space-y-1">
                          <div className="flex items-center justify-between text-[9px] font-semibold text-slate-400">
                            <span>Step {progress.step}/6</span>
                            <span>{progress.pct}%</span>
                          </div>
                          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-500 transition-all duration-500"
                              style={{ width: `${progress.pct}%` }}
                            />
                          </div>
                        </div>

                        {/* Next Hearing Date (NDOH) Pill */}
                        <div className="mt-2.5 flex items-center justify-between text-[11px] pt-2 border-t border-slate-750">
                          <div className="flex items-center gap-1 text-emerald-400 font-mono text-[10px] font-semibold">
                            <Calendar className="w-3 h-3 shrink-0" />
                            <span>NDOH: {c.next_hearing_date}</span>
                          </div>

                          {userRole !== "clerk" && (
                            <span className="font-mono font-bold text-white text-[11px]">
                              {c.currency === "USD" ? `$${c.retainer_amount}` : `₹${c.retainer_amount.toLocaleString("en-IN")}`}
                            </span>
                          )}
                        </div>

                        {/* Stage Shift Controls */}
                        <div
                          className="mt-2.5 pt-2 border-t border-slate-800 flex items-center justify-between text-[10px] text-slate-400"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="truncate max-w-[120px]">
                            {c.lead_counsel}
                          </span>

                          <div className="flex items-center gap-1">
                            {stageIdx > 0 && (
                              <button
                                title={`Move back to ${STAGES[stageIdx - 1].title}`}
                                onClick={() => onAdvanceStage(c, STAGES[stageIdx - 1].id)}
                                className="p-1 rounded hover:bg-slate-800 hover:text-white transition-colors"
                              >
                                <ChevronLeft className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              title="Open Case Binder"
                              onClick={() => onSelectCase(c)}
                              className="p-1 rounded hover:bg-slate-800 text-amber-400 hover:text-amber-300 transition-colors"
                            >
                              <FolderOpen className="w-3.5 h-3.5" />
                            </button>
                            {stageIdx < STAGES.length - 1 && (
                              <button
                                title={`Advance to ${STAGES[stageIdx + 1].title}`}
                                onClick={() => onAdvanceStage(c, STAGES[stageIdx + 1].id)}
                                className="p-1 rounded hover:bg-slate-800 hover:text-white transition-colors"
                              >
                                <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Column Pagination Expand Button */}
                  {hiddenCount > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setColumnLimits((prev) => ({
                          ...prev,
                          [stage.id]: (prev[stage.id] || 25) + 50,
                        }))
                      }
                      className="w-full py-2 px-3 rounded-xl bg-slate-800 text-slate-300 text-[11px] font-bold hover:bg-amber-500/20 hover:text-amber-300 transition-all border border-slate-700 flex items-center justify-center gap-1.5"
                    >
                      Load More (+{hiddenCount} matters)
                    </button>
                  )}

                  {/* Dedicated Bottom Drop Zone - Drop Anywhere */}
                  <div
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDrop={(e) => handleDrop(e, stage.id)}
                    className={`w-full py-3.5 px-2 rounded-xl border-2 border-dashed transition-all flex items-center justify-center text-center text-[10px] ${
                      isTargeted
                        ? "border-amber-500 bg-amber-500/15 text-amber-400 font-bold shadow-inner"
                        : "border-slate-800 text-slate-500 hover:border-slate-700"
                    }`}
                  >
                    {isTargeted ? (
                      <span>⚡ Drop here to move to {stage.title}</span>
                    ) : (
                      <span>+ Drop anywhere in column</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
